import { Box, For, HStack, Text, VStack } from '@chakra-ui/react';
import InfoValue from '@components/map/popup/infos/InfoValue';
import { DraggablePanel } from '@components/ui/draggable';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { selectCoordinatesUnit } from '@redux/settings/settings.selectors';
import { selectSectionByPerimeterCameraKey } from '@redux/situation/monitoring.selectors';
import { AngleUnits, DistanceUnits, formatUnit } from '@utils/unit.utils';
import { formatCoordinatesToString } from '@utils/validation/coordinates.utils';
import DmsCoordinates from 'dms-conversion';
import { FormattedMessage, useIntl } from 'react-intl';

import { PerimeterCameraInfoData, PopupPositionData } from '@/types/popup.types';

export default function PerimeterCameraInfo({
  mapConstraintsRef,
  featureValue,
  top,
  left,
  onClose,
}: Readonly<PerimeterCameraInfoData & PopupPositionData>) {
  const { formatMessage } = useIntl();
  const userSettingsUnit = useAppSelector(selectCoordinatesUnit);
  const sections = useSelectorWithReplayMode(selectSectionByPerimeterCameraKey, featureValue.uniqueKey);
  const notSpecified = formatMessage({ id: 'global.noInformation' });

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="perimeterCameras.perimeterCameraInfo" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={{ base: '460px', '4k': '580px' }}>
        <VStack backgroundColor="neutral.700" width="100%" gap={0.25}>
          <Box padding={4} width="100%">
            <HStack gap={4} width="100%">
              <VStack gap={4} flexGrow={1} alignItems="start">
                <InfoValue label={formatMessage({ id: 'perimeterCameras.name' })} value={featureValue.name} />
                <VStack width="100%" gap={1} alignItems="start">
                  <Text textStyle="md" fontWeight="medium" color="neutral.300">
                    <FormattedMessage id="perimeterCameras.fov" />
                  </Text>
                  <HStack>
                    <Text textStyle="md" fontWeight="medium" color="neutral.300">
                      <FormattedMessage id="perimeterCameras.angle" />
                    </Text>
                    <Text textStyle="md" fontWeight="medium">
                      {formatUnit(AngleUnits.TURN, featureValue.fov)}
                    </Text>
                  </HStack>
                  <HStack>
                    <Text textStyle="md" fontWeight="medium" color="neutral.300">
                      <FormattedMessage id="perimeterCameras.range" />
                    </Text>
                    <Text textStyle="md" fontWeight="medium">
                      {formatUnit(DistanceUnits.METERS, featureValue.range)}
                    </Text>
                  </HStack>
                </VStack>
                <VStack width="100%" gap={1} alignItems="start">
                  <Text textStyle="md" fontWeight="medium" color="neutral.300">
                    <FormattedMessage id="perimeterCameras.sections" />
                  </Text>
                  <For each={sections}>
                    {(section) => (
                      <Text key={section} textStyle="md" fontWeight="medium">
                        {section}
                      </Text>
                    )}
                  </For>
                </VStack>
              </VStack>
            </HStack>
          </Box>
          <Box padding={4} width="100%" backgroundColor="neutral.800">
            <VStack gap={3} flexGrow={1} alignItems="start">
              <InfoValue label={formatMessage({ id: 'perimeterCameras.grid' })} value={notSpecified} />
              {featureValue.coordinates !== null && (
                <InfoValue
                  label={formatMessage({ id: 'perimeterCameras.latLong' })}
                  value={formatCoordinatesToString(
                    new DmsCoordinates(featureValue.coordinates.latitude, featureValue.coordinates.longitude),
                    userSettingsUnit,
                  )}
                />
              )}
            </VStack>
          </Box>
          <Box padding={4} width="100%" backgroundColor="neutral.800">
            <InfoValue label={formatMessage({ id: 'perimeterCameras.technicalContact' })} value={notSpecified} />
          </Box>
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
